import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Modal, { ModalButton, ModalSecondaryButton } from "../../components/Modal";
import { classNames } from "../../utils/styles";
import { KeepoList } from "./styles";

type Keepo = { siteId: string; title: string; url: string };

type Props = {
  domain: string;
  /** Only the PLUS Keepos: a domain of your own is PLUS. */
  keepos: Keepo[];
  /** The Keepo connected now, if any: picked from the start. */
  current: string | null;
  isSaving: boolean;
  error?: string;
  onConnect: (siteId: string) => void;
  onClose: () => void;
};

/**
 * Which Keepo the domain opens: the PLUS Keepos to pick from (the connected one picked), and a bar at the bottom
 * with Cancel and Connect. Without a PLUS Keepo, it says so and offers PLUS.
 */
export const ConnectKeepoModal = ({ domain, keepos, current, isSaving, error, onConnect, onClose }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prefix = "pages.domains.detail.keepo.modal";
  const [selected, setSelected] = useState<string | null>(current);

  const canConnect = !!selected && selected !== current && !isSaving;

  return (
    <Modal
      outsideExit
      busy={isSaving}
      onExit={onClose}
      title={t(`${prefix}.title`)}
      subtitle={t(`${prefix}.subtitle`, { domain })}
      exitLabel={t(`${prefix}.close`)}
      error={error}
      footer={
        <>
          <ModalSecondaryButton type="button" disabled={isSaving} onClick={onClose}>
            {t(`${prefix}.cancel`)}
          </ModalSecondaryButton>
          {keepos.length ? (
            <ModalButton
              loading={isSaving}
              disabled={!canConnect}
              onClick={() => canConnect && selected && onConnect(selected)}>
              {t(`${prefix}.connect`)}
            </ModalButton>
          ) : (
            <ModalButton onClick={() => navigate("/subscription/features")}>{t(`${prefix}.subscribe`)}</ModalButton>
          )}
        </>
      }>
      <KeepoList>
        {keepos.length ? (
          <ul role="radiogroup" aria-label={t(`${prefix}.title`)}>
            {keepos.map(keepo => {
              const isSelected = selected === keepo.siteId;
              return (
                <li key={keepo.siteId}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    className={classNames("keepo", { selected: isSelected })}
                    disabled={isSaving}
                    onClick={() => setSelected(keepo.siteId)}>
                    <span className="avatar" aria-hidden="true">
                      {(keepo.title || keepo.url).trim().charAt(0).toUpperCase()}
                    </span>
                    <span className="text">
                      <b>{keepo.title || keepo.url}</b>
                      <span>{`${keepo.url}.keepo.bio`}</span>
                    </span>
                    {keepo.siteId === current && <span className="current">{t(`${prefix}.current`)}</span>}
                    <span className="radio" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="empty">{t(`${prefix}.noPlus`)}</p>
        )}
      </KeepoList>
    </Modal>
  );
};
